import React from 'react'
import useSWR from 'swr'
import useSWRImmutable from 'swr/immutable'

import { backendFetch, backendFetchResult, backendFetchResultData } from '../common/api'

export const lowInventoryActionNone = 'none'
export const lowInventoryActionSkipOrder = 'skip-order'
export const lowInventoryActionPauseSubscription = 'pause-subscription'
export type LowInventoryAction =
  | typeof lowInventoryActionNone
  | typeof lowInventoryActionSkipOrder
  | typeof lowInventoryActionPauseSubscription

export const failedDunningActionCancelSubscription = 'cancel-subscription'
export const failedDunningActionSkipOrder = 'skip-order'
export const failedDunningActionPauseSubscription = 'pause-subscription'
export type FailedDunningAction =
  | typeof failedDunningActionCancelSubscription
  | typeof failedDunningActionSkipOrder
  | typeof failedDunningActionPauseSubscription
export type SubscriptionDeliveryAddressAutoUpdate =
  | 'disabled'
  | 'enabled-without-price'
  | 'enabled-with-price'
export type CustomerAttemptBillingBehavior =
  | 'advance-to-next-billing-date'
  | 'leave-unchanged'
  | 'adjust-to-current-day'

export interface UserSettings {
  customers_can_add_products_from_widget: boolean
  send_subscription_delivery_address_auto_update_failed_emails: boolean
  subscription_delivery_address_auto_update: SubscriptionDeliveryAddressAutoUpdate
  attempt_retry_count: number
  attempt_retry_delay: number
  attempt_timeout: number
  low_inventory_action: LowInventoryAction
  low_inventory_threshold: number
  failed_dunning_action: FailedDunningAction
  send_user_emails_to: string | null
  send_user_subscription_emails: boolean
  callback_url: string
  callback_topics: string[]
  merchant_api_ip_allowlist_enabled: boolean
  klaviyo_api_key: string
  klaviyo_enabled: boolean
  analytics_start_date: string | null
  getting_started_card_dismissed: boolean
}

export interface CustomerPortalSettings {
  customers_can_add_products_from_widget: boolean
  standalone_customer_portal: boolean
  show_link_to_customer_portal_after_checkout: boolean
  customers_can_skip_installments: boolean
  customers_can_attempt_billings: boolean
  customer_attempt_billing_behavior: CustomerAttemptBillingBehavior
  customers_can_cancel_subscriptions: boolean
  customers_can_pause_subscriptions: boolean
  customers_can_reactivate_subscriptions: boolean
  customer_subscription_reactivation_period: number
  customers_can_change_quantity: string
  customers_can_add_products: boolean
  customers_can_remove_products: boolean
  customers_can_swap_products: string
  customers_can_edit_discounts: boolean
  customers_can_change_selling_plan: boolean
  customer_portal_extension_enabled: boolean
  customer_portal_theme: string
  send_new_subscription_emails: boolean
  send_new_subscription_no_payment_method_emails: boolean
  send_subscription_expired_emails: boolean
  send_subscription_paused_emails: boolean
  send_subscription_resumed_emails: boolean
  send_subscription_reactivation_emails: boolean
  send_subscription_edited_emails: boolean
  send_subscription_retrying_emails: boolean
  send_subscription_failed_emails: boolean
  send_annual_reminder_emails: boolean
  annual_reminder_emails_days: number
  send_payment_notification_emails: boolean
  send_payment_notification_no_payment_method_emails: boolean
  payment_notification_timing: number
  send_address_changed_emails: boolean
  send_invite_emails: boolean
  cancellation_flow_show_survey: boolean
  cancellation_flow_offer_pause: boolean
  cancellation_flow_offer_skip_order: boolean
  cancellation_flow_offer_discount: boolean
  cancellation_flow_discount_code: string
  cancellation_flow_offer_edit: boolean
}

export const SettingsContext = React.createContext<UserSettings | null>(null)

export interface ThemeColors {
  [key: string]: string
}

export interface KlaviyoAuthorizationStatus {
  isAuthorized: boolean
  organizationName: string | null
}

export type KlaviyoBulkProfileImportStatus = 'Dispatching' | 'InProgress' | 'Completed' | null

export const useUserSettings = () =>
  useSWR<UserSettings>(['user-settings'], () =>
    backendFetch('GET', '/settings/user-settings').then((resp: any) => resp.data)
  )

export const patchUserSettings = async (settings: UserSettings) => {
  const body = JSON.stringify({ settings })
  return await backendFetchResult('PATCH', '/settings/user-settings', { body })
}

export const useCustomerPortalSettings = () =>
  useSWR<CustomerPortalSettings>(['customer-portal-settings'], () =>
    backendFetch('GET', '/settings/user-settings').then((resp: any) => resp.data)
  )

export const patchCustomerPortalSettings = async (settings: CustomerPortalSettings) => {
  const body = JSON.stringify({ settings })
  return await backendFetchResult('PATCH', '/settings/user-settings', { body })
}

export const useCustomerPortalCustomStyles = () =>
  useSWRImmutable<string>(['customer-portal-custom-styles'], () =>
    backendFetchResultData<string>('GET', '/settings/customer-portal-custom-styles')
  )

export const useCustomerPortalCustomScriptUrl = () =>
  useSWRImmutable<string>(['customer-portal-custom-script-url'], () =>
    backendFetchResultData<string>('GET', '/settings/customer-portal-custom-script-url')
  )

export const useCustomerPortalCustomColors = () =>
  useSWRImmutable<ThemeColors>(['customer-portal-custom-colors'], () =>
    backendFetchResultData<ThemeColors>('GET', '/settings/customer-portal-custom-colors')
  )

export const fetchKlaviyoAuthorizationStatus = (_: string) =>
  backendFetchResultData<KlaviyoAuthorizationStatus>('GET', '/klaviyo/authorization-status')

export const useKlaviyoAuthorizationStatus = () =>
  useSWR<KlaviyoAuthorizationStatus>(
    ['klaviyo/authorization-status'],
    fetchKlaviyoAuthorizationStatus
  )

export const fetchKlaviyoBulkProfileImportStatus = (_: string) =>
  backendFetchResultData<KlaviyoBulkProfileImportStatus>(
    'GET',
    '/klaviyo/bulk-profile-import-status'
  )

export const useKlaviyoBulkProfileImportStatus = (polling: boolean) =>
  useSWR<KlaviyoBulkProfileImportStatus>(
    ['klaviyo/bulk-profile-import-status'],
    fetchKlaviyoBulkProfileImportStatus,
    {
      refreshInterval: polling ? 5000 : undefined,
    }
  )

export type ReportSettings = Pick<UserSettings, 'analytics_start_date'>

export const updateReportSettings = async (settings: ReportSettings) => {
  const body = JSON.stringify({ ...settings })
  return await backendFetchResult('POST', '/dashboard/report-settings', { body })
}
